import { ui } from "@/components/ui";
import {
	createUniqueInjectionOptions,
	toFilteredUniqueInjectionOptions,
} from "@/features/injection/utils/injectionOptions";
import { services as s } from "@/services";
import { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { formComponents as fc } from "../../../components/form";
import GroupedFields from "../../../components/form/GroupedFields";
import { sideOptions } from "../../events/utils/sideOptions";
import useInjectionDetailsOptions from "../hooks/useInjectionDetailsOptions";
import InjectionContentsForm from "./InjectionContentsForm";
import InjectionLocationForm from "./InjectionLocationForm";
import { Copy, Plus, Trash2 } from "lucide-react";
import { cn } from "../../../lib/utils";

// Define the schema type for injection detail
export interface InjectionDetailSchemaT {
	injection_detail_id: string;
	injection_id: string;
	side?: string | null;
	orthopaedic_area?: string | null;
	site?: string | null;
	subsite?: string | null;
	injection_substance?: string | null;
	dose?: string | null;
	unit?: string | null;
}

// Define the form schema type for the parent form
export interface InjectionFormSchemaT {
	injection_details: InjectionDetailSchemaT[];
}

interface Props {
	injectionId: string;
	className?: string;
}

const InjectionDetailsFormFields = ({ injectionId, className }: Props) => {
	const form = useFormContext();
	const { injectionLocations, injectionContents } =
		useInjectionDetailsOptions();

	// Safely watch injection_details with a fallback
	const formValues = form.watch("injection_details") || [];

	// Use proper typing for useFieldArray
	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: "injection_details",
	});

	// Set initial data when fields are empty
	useEffect(() => {
		if (!fields || fields.length === 0) {
			append({
				injection_id: injectionId,
				injection_detail_id: s.uuidv4(),
				side: "",
				orthopaedic_area: "",
				site: "",
				subsite: "",
				injection_substance: "",
				dose: "",
				unit: "",
			} as InjectionDetailSchemaT);
		}
	}, [fields, append, injectionId]);

	// Early return if fields is empty (not initialized yet)
	if (!fields || fields.length === 0) {
		return <div>Loading injection details form...</div>;
	}

	// Ensure we have data for injectionLocations and injectionContents
	const safeInjectionLocations = injectionLocations || [];
	const safeInjectionContents = injectionContents || [];

	return (
		<div className={cn("w-full space-y-4 text-lg", className)}>
			{fields.map((field, index) => (
				<div
					className="flex flex-col gap-y-4 bg-indigo-50 dark:bg-slate-900 shadow-md p-4 border border-slate-300 rounded-lg"
					key={field.id}
				>
					<div className="flex flex-row justify-between items-center">
						<h3 className="text-slate-900 dark:text-slate-100">
							Injection {index + 1}
						</h3>
						<div className="flex flex-row flex-wrap mt-2">
							<ui.Button
								size="icon"
								type="button"
								variant="ghost"
								onClick={() => {
									remove(index);
								}}
								disabled={fields.length === 1}
							>
								<Trash2 size={20} />
							</ui.Button>
							<ui.Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={() => {
									append({
										injection_id: injectionId,
										injection_detail_id: s.uuidv4(),
										side: formValues[index]?.side || "",
										orthopaedic_area: formValues[index]
											?.orthopaedic_area || "",
										site: formValues[index]?.site || "",
										subsite: formValues[index]
											?.subsite || "",
										injection_substance: formValues[index]
											?.injection_substance || "",
										dose: formValues[index]?.dose || "",
										unit: formValues[index]?.unit || "",
									} as InjectionDetailSchemaT);
								}}
							>
								<Copy size={20} />
							</ui.Button>
						</div>
					</div>

					<div className="flex md:flex-row flex-col gap-x-2 gap-y-4">
						<fc.Select
							name={`injection_details.${index}.side`}
							label="Side"
							options={sideOptions}
							clearable={true}
						/>
						<fc.ComboBox
							name={`injection_details.${index}.orthopaedic_area`}
							label="Anatomic Area"
							options={createUniqueInjectionOptions(
								safeInjectionLocations,
								"orthopaedic_area",
							)}
							addOption={<InjectionLocationForm />}
							clearable={true}
						/>
					</div>
					<GroupedFields>
						<fc.ComboBox
							name={`injection_details.${index}.site`}
							label="Injection Site"
							options={toFilteredUniqueInjectionOptions(
								safeInjectionLocations,
								"site",
								"orthopaedic_area",
								formValues[index]?.orthopaedic_area || "",
							)}
							addOption={<InjectionLocationForm />}
							clearable={true}
						/>
						<fc.ComboBox
							name={`injection_details.${index}.subsite`}
							label="Injection Subsite"
							options={toFilteredUniqueInjectionOptions(
								safeInjectionLocations,
								"subsite",
								"site",
								formValues[index]?.site || "",
							)}
							addOption={<InjectionLocationForm />}
							clearable={true}
						/>
					</GroupedFields>
					<fc.ComboBox
						name={`injection_details.${index}.injection_substance`}
						label="Injection Substance"
						options={createUniqueInjectionOptions(
							safeInjectionContents,
							"injection_substance",
						)}
						addOption={<InjectionContentsForm />}
						clearable={true}
					/>
					<GroupedFields>
						<>
							<fc.ComboBox
								name={`injection_details.${index}.dose`}
								label="Dose"
								options={toFilteredUniqueInjectionOptions(
									safeInjectionContents,
									"dose",
									"injection_substance",
									formValues[index]?.injection_substance ||
										"",
									true,
								)}
								addOption={<InjectionContentsForm />}
								clearable={true}
							/>
							<fc.ComboBox
								name={`injection_details.${index}.unit`}
								label="Unit"
								options={toFilteredUniqueInjectionOptions(
									safeInjectionContents,
									"unit",
									"dose",
									formValues[index]?.dose || "",
								)}
								addOption={<InjectionContentsForm />}
								clearable={true}
							/>
						</>
					</GroupedFields>
				</div>
			))}
		</div>
	);
};

export default InjectionDetailsFormFields;
